import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule, MatPrefix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  throwError,
  timer,
} from 'rxjs';

import { getContrastTextColor } from '../../ui/forms/color-picker';
import { PhoneInputComponent } from '../../ui/forms/phone-input';
import { ToastComponent } from '../../ui/toast';
import { WidgetConfiguration } from '../models';

@Component({
  selector: 'rms-widget',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './widget.component.html',
  styleUrl: './widget.component.scss',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatPrefix,
    PhoneInputComponent,
    ToastComponent,
  ],
})
export class WidgetComponent {
  @Input() public lang: 'en' | 'it' = 'en';
  @Input() public previewMode = false;

  @Input() public set config(val: WidgetConfiguration | null) {
    this.config$.next(val);
  }

  protected readonly config$ = new BehaviorSubject<WidgetConfiguration | null>(null);

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = this.fb.group({
    name: this.fb.control(''),
    email: this.fb.control(''),
    phone: this.fb.control('', Validators.required),
  });

  protected readonly loading$ = new BehaviorSubject(false);
  protected readonly error$ = new BehaviorSubject(false);
  protected readonly submitted$ = new BehaviorSubject(false);
  protected readonly formStatus$ = this.form.statusChanges.pipe(startWith(this.form.status));

  protected readonly buttonTextColor$ = this.config$.pipe(map((c) => getContrastTextColor(c?.styles?.accentColor)));

  protected readonly logoUrl$ = this.config$.pipe(
    map((c) => c?.logo?.source),
    distinctUntilChanged(),
    switchMap((source) => {
      if (!source) {
        return of('/assets/img/logo/logo.svg');
      }

      if (!(source instanceof File)) {
        return of(typeof source === 'string' ? source : null);
      }

      return new Observable<string>((subscriber) => {
        const url = URL.createObjectURL(source);
        subscriber.next(url);

        return () => URL.revokeObjectURL(url);
      });
    })
  );

  constructor() {
    this.config$.pipe(takeUntilDestroyed()).subscribe((config) => this.updateValidators(config));
  }

  protected submit(): void {
    if (this.previewMode || this.form.invalid) return;
    this.loading$.next(true);
    this.error$.next(false);

    // Mock POST — replace with WidgetService.createCallRequest() later
    // Swap throwError for of(undefined) to test the success path
    timer(1500)
      .pipe(
        switchMap(() => throwError(() => new Error())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.loading$.next(false);
          this.submitted$.next(true);
        },
        error: () => {
          this.loading$.next(false);
          this.error$.next(true);
        },
      });
  }

  private updateValidators(config: WidgetConfiguration | null): void {
    if (config?.content?.nameAvailable) {
      this.form.controls.name.setValidators(Validators.required);
    } else {
      this.form.controls.name.clearValidators();
    }
    if (config?.content?.emailAvailable) {
      this.form.controls.email.setValidators([Validators.required, Validators.email]);
    } else {
      this.form.controls.email.clearValidators();
    }
    this.form.controls.name.updateValueAndValidity();
    this.form.controls.email.updateValueAndValidity();
  }
}
